<template>
  <div class="w-full">
    <div class="prose max-w-3xl mx-auto px-6 py-16">
      <NuxtLink to="/updates" class="text-secondary hover:text-primary text-sm mb-6 inline-block">
        ← All updates
      </NuxtLink>

      <article v-if="update">
        <header class="mb-8">
          <span class="badge badge-primary badge-lg mb-2">{{ update.version }}</span>
          <h1 class="text-3xl font-bold text-primary mt-2">{{ update.title }}</h1>
          <p class="text-secondary mt-1">{{ update.date }}</p>
        </header>

        <div
          v-if="update.videoId"
          class="aspect-video rounded-lg overflow-hidden bg-base-200 mb-8 not-prose"
        >
          <iframe
            :src="`https://www.youtube.com/embed/${update.videoId}`"
            :title="`${update.title} - video`"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            class="w-full h-full"
          />
        </div>

        <div class="markdown-content text-secondary" v-html="renderedContent" />

      </article>

      <p class="text-secondary text-sm mt-12">
        Have feedback or feature ideas?
        <a href="mailto:hello@supapm.com" class="link link-primary">Get in touch</a>.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { micromark } from 'micromark'
import { getUpdateBySlug, type Update } from '@/data/updates'

const route = useRoute()
const slug = route.params.slug as string
const update = ref<Update | undefined>(getUpdateBySlug(slug))

if (!update.value) {
  throw createError({ statusCode: 404, message: 'Update not found' })
}

const renderedContent = computed(() => micromark(update.value!.content))

useSeoMeta({
  title: `${update.value!.title} - SupaPM Updates`,
  description: update.value!.description,
  ogTitle: `${update.value!.title} - SupaPM Updates`,
  ogDescription: update.value!.description,
})
</script>

<style scoped>
.markdown-content :deep(p) {
  margin-bottom: 1rem;
}
.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 1rem 0;
  padding-left: 1.5rem;
}
.markdown-content :deep(li) {
  margin: 0.25rem 0;
}
.markdown-content :deep(h2) {
  font-size: 1.125rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}
.markdown-content :deep(a) {
  @apply text-primary link link-primary;
}
</style>
